import React from "react";

import { ArrowRight, Building2, ExternalLink } from "lucide-react";

import SynopsisSectionCard from "./SynopsisSectionCard";
import Button from "../../../Components/Button";
import type { AccountSummary } from "../../../Store/Reducers/AccountWorkspaceSlice";
import { useAccountTiers } from "../../../Utils/Helper/accountTierHelpers";
import { formatDateUSShort, getInitials } from "../../../Utils/Helper/Helper";
import { formatCompactCurrencyDisplay } from "../../AccountWorkspace/utils/formatCompactCurrency";

type HeaderCommercialSectionProps = {
  account: AccountSummary | null;
  loading: boolean;
  onOpenWorkspace: () => void;
  onOpenAssess?: () => void;
  assessDisabled?: boolean;
};

/** Shared sizing so Assess + Workspace align as a matched action pair. */
const headerActionBase =
  "box-border inline-flex !h-8 !min-h-0 items-center justify-center gap-1.5 !rounded-lg !border !px-3.5 !py-0 text-[13px] font-medium leading-none";

const assessBtnClass = `${headerActionBase} !border-amber-200 !bg-amber-50 !text-amber-800 hover:!border-amber-400 hover:!bg-amber-100 hover:!text-amber-900 disabled:!cursor-not-allowed disabled:!opacity-50`;

const workspaceBtnClass = `${headerActionBase} !border-transparent`;

/** Same fallback as Account Workspace Overview metrics. */
const NA = "NA";

const formatRenewalDisplay = (
  renewalDate: string | null | undefined
): string => {
  if (renewalDate == null || renewalDate === "") return NA;
  return formatDateUSShort(renewalDate, true);
};

const formatMetricValue = (
  value: string | number | null | undefined
): string => {
  if (value == null || value === "") return NA;
  return String(value);
};

/** Display assessment level as AL3 (from L3, 3, al3, etc.). */
const formatAlignmentLevel = (
  value: string | number | null | undefined
): string => {
  if (value == null || value === "") return "AL ?";
  const raw = String(value).trim();
  if (!raw || /^(-|—|–|n\/?a|unknown|none|null)$/i.test(raw)) return "AL ?";
  const match = raw.match(/^A?L?\s*(\d+)$/i);
  if (match) return `AL ${match[1]}`;
  if (raw.toUpperCase().startsWith("AL")) {
    const spaced = raw.toUpperCase().replace(/^AL\s*/i, "AL ");
    return spaced === "AL " ? "AL ?" : spaced;
  }
  return `AL ${raw}`;
};

const MetricCell: React.FC<{
  label: string;
  value: string;
  valueClassName?: string;
  isLast?: boolean;
  title?: string;
}> = ({
  label,
  value,
  valueClassName = "text-gray-900",
  isLast = false,
  title,
}) => (
  <div
    className={`min-w-0 flex-1 px-4 py-3.5 ${isLast ? "" : "border-r border-gray-200"}`}
  >
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div
      className={`mt-1.5 truncate text-base font-medium leading-none ${valueClassName}`}
      title={title}
    >
      {value}
    </div>
  </div>
);

const HeaderCommercialSection: React.FC<HeaderCommercialSectionProps> = ({
  account,
  loading,
  onOpenWorkspace,
  onOpenAssess,
  assessDisabled = false,
}) => {
  const { formatTier } = useAccountTiers();
  const accountName =
    account?.account_name || (loading ? "Loading…" : "Account");
  const metaParts = [account?.industry, account?.region, account?.city].filter(
    Boolean
  );

  const revenueValue =
    account?.revenue == null
      ? NA
      : formatCompactCurrencyDisplay(account.revenue);
  const accountLevelValue = formatAlignmentLevel(account?.assessment_level);

  return (
    <SynopsisSectionCard icon={Building2} title="Account Overview">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3.5">
          <div
            className="flex size-14 shrink-0 items-center justify-center rounded-full border-2 border-gray-200 bg-white text-sm font-semibold text-gray-500 shadow-sm"
            aria-hidden
          >
            {loading && !account?.account_name ? "—" : getInitials(accountName)}
          </div>
          <div className="min-w-0">
            <h3 className="m-0 text-xl font-medium leading-tight text-gray-900">
              {accountName}
            </h3>
            <p className="m-0 mt-2 text-sm text-gray-500">
              {metaParts.length ? metaParts.join(" · ") : "—"}
              {account?.tier ? (
                <>
                  {" · "}
                  <span className="inline-flex rounded bg-violet-50 px-1.5 py-0.5 text-xs font-medium text-violet-700">
                    {formatTier(account.tier)}
                  </span>
                </>
              ) : null}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {onOpenAssess ? (
            <Button
              variant="secondary"
              onClick={onOpenAssess}
              disabled={assessDisabled}
              className={assessBtnClass}
            >
              Assess
              <ArrowRight
                size={13}
                strokeWidth={2.25}
                className="shrink-0"
                aria-hidden
              />
            </Button>
          ) : null}
          <Button
            variant="primary"
            onClick={onOpenWorkspace}
            className={workspaceBtnClass}
          >
            Workspace
            <ExternalLink
              size={13}
              strokeWidth={2.25}
              className="shrink-0"
              aria-hidden
            />
          </Button>
        </div>
      </div>

      <div className="flex w-full overflow-hidden rounded-md border border-gray-200 bg-gray-50">
        <MetricCell
          label="Revenue"
          value={revenueValue}
          valueClassName="text-green-600"
        />
        <MetricCell
          label="Renewal"
          value={formatRenewalDisplay(account?.renewal_date)}
          valueClassName="text-amber-600"
        />
        <MetricCell
          label="Tier"
          value={formatMetricValue(
            account?.tier ? formatTier(account.tier) : account?.tier
          )}
        />
        <MetricCell
          label="Account Level"
          value={accountLevelValue}
          title={
            /^(AL|RL)\s*\?$/i.test(accountLevelValue) ? "Unassessed" : undefined
          }
        />
        <MetricCell
          label="Region"
          value={formatMetricValue(account?.region)}
          isLast
        />
      </div>
    </SynopsisSectionCard>
  );
};

export default HeaderCommercialSection;
